/**
 * Walkable bounds: an optional per-world rectangle (world metres, x/z) that
 * players can never leave — walking, the phone joystick, VR stick / room-scale
 * walking, snap turns and teleports all end up inside it.
 *
 * Saved as `worlds.settings.bounds = { min: [x, z], max: [x, z] }`. It is a
 * safety net under the collision geometry: a room's walls already stop you,
 * but an AABB push-out, a hop or a seat snap can still leave you outside a
 * venue, and an open pavilion has nothing to stop you at all.
 *
 * The rectangle is where the player's centre (desktop) or head (VR) may be,
 * so it should already sit a body-width inside the walls.
 */
export interface WorldBounds {
  min: [number, number];
  max: [number, number];
}

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** Validate a saved `settings.bounds` value; anything malformed means "no bounds". */
export function parseWorldBounds(v: unknown): WorldBounds | null {
  const b = v as { min?: unknown; max?: unknown } | null;
  if (!b || !Array.isArray(b.min) || !Array.isArray(b.max)) return null;
  const [x0, z0] = b.min, [x1, z1] = b.max;
  if (!finite(x0) || !finite(z0) || !finite(x1) || !finite(z1)) return null;
  if (x1 - x0 < 0.5 || z1 - z0 < 0.5) return null;
  return { min: [x0, z0], max: [x1, z1] };
}

/** Is (x, z) inside the bounds (shrunk by `margin` on every side)? No bounds = everywhere is inside. */
export function insideBounds(b: WorldBounds | null | undefined, x: number, z: number, margin = 0): boolean {
  if (!b) return true;
  return x >= b.min[0] + margin && x <= b.max[0] - margin && z >= b.min[1] + margin && z <= b.max[1] - margin;
}

/** The nearest point to (x, z) inside the bounds; (x, z) itself when there are none. */
export function clampToBounds(b: WorldBounds | null | undefined, x: number, z: number, margin = 0): [number, number] {
  if (!b) return [x, z];
  const lo = (i: 0 | 1) => b.min[i] + margin, hi = (i: 0 | 1) => b.max[i] - margin;
  // A margin wider than the box collapses it to its centre line.
  const cx = lo(0) > hi(0) ? (b.min[0] + b.max[0]) / 2 : Math.min(hi(0), Math.max(lo(0), x));
  const cz = lo(1) > hi(1) ? (b.min[1] + b.max[1]) / 2 : Math.min(hi(1), Math.max(lo(1), z));
  return [cx, cz];
}
